import * as React from "react";
import { useNavigate } from "react-router";
import { useSmartBack } from "../hooks/smartBack";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  ArrowUndo20Regular,
  Delete20Regular,
  Search20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";
import { useAuth } from "../context/AuthContext";
import { contactTypeLabel } from "../utils/contactTypeLabel";
import {
  restoreMergeLoser,
  purgeMergeLoser,
} from "../services/duplicateCandidateService";
import { bestaetigen } from "../components/redesign/bestaetigen";

interface TrashEntry {
  source: "contact" | "merge";
  id: number;
  mergeHistoryId: number | null;
  winnerContactId: number | null;
  type: string;
  name: string;
  street: string;
  city: string;
  responsibility: string;
  reason: string;
  deletedAt: string | null;
}

const PAGE_SIZE = 50;
const GRID =
  "90px minmax(160px, 1.4fr) 120px minmax(140px, 1.4fr) 150px 110px 96px auto";

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "48px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1280px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    ...shorthands.padding("20px", "24px"),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
  },
  subtitle: {
    color: "rgba(255,255,255,0.85)",
    fontSize: "0.9rem",
    marginTop: "6px",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
  },
  search: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ...shorthands.padding("8px", "12px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.white,
    flexGrow: 1,
    minWidth: "220px",
    maxWidth: "420px",
  },
  searchInput: {
    ...shorthands.border("0"),
    outlineStyle: "none",
    flexGrow: 1,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: "transparent",
  },
  count: { color: BRAND.muted, fontSize: "0.85rem", marginLeft: "auto" },
  pager: { display: "inline-flex", alignItems: "center", columnGap: "8px" },
  card: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    ...shorthands.overflow("hidden"),
  },
  head: {
    display: "grid",
    gridTemplateColumns: GRID,
    columnGap: "12px",
    ...shorthands.padding("10px", "14px"),
    backgroundColor: BRAND.bg,
    fontSize: "0.74rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: BRAND.muted,
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  row: {
    display: "grid",
    gridTemplateColumns: GRID,
    columnGap: "12px",
    alignItems: "center",
    ...shorthands.padding("10px", "14px"),
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    fontSize: "0.86rem",
    ":hover": { backgroundColor: BRAND.bg },
  },
  cellName: { fontWeight: 600, minWidth: 0, overflowWrap: "anywhere" },
  cellMuted: { color: BRAND.muted, minWidth: 0, overflowWrap: "anywhere" },
  typeBadge: {
    display: "inline-block",
    ...shorthands.padding("2px", "8px"),
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.bg,
    fontSize: "0.72rem",
    fontWeight: 600,
  },
  reasonBadge: {
    display: "inline-block",
    ...shorthands.padding("2px", "8px"),
    ...shorthands.borderRadius("999px"),
    fontSize: "0.72rem",
    fontWeight: 600,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  actions: { display: "flex", columnGap: "8px", justifyContent: "flex-end" },
  btn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    ...shorthands.padding("6px", "10px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    whiteSpace: "nowrap",
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  btnRestore: { ...shorthands.borderColor(BRAND.purple), color: BRAND.purple },
  btnPurge: { ...shorthands.borderColor("#c0392b"), color: "#c0392b" },
  pagerBtn: {
    appearance: "none",
    cursor: "pointer",
    ...shorthands.padding("6px", "12px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ":disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  empty: { color: BRAND.muted, fontStyle: "italic", ...shorthands.padding("16px") },
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    ...shorthands.padding("10px", "14px"),
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
});

const keyOf = (e: TrashEntry) => `${e.source}:${e.id}`;
const fmtDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");

const PapierkorbPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const goBack = useSmartBack("/kontakte");
  const { isAdmin, isGfgl } = useAuth();
  const allowed = isAdmin || isGfgl;

  const [rows, setRows] = React.useState<TrashEntry[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const [q, setQ] = React.useState("");
  const [qDeb, setQDeb] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);

  // Debounce the search box (→ server `?q=`), reset to first page on change.
  React.useEffect(() => {
    const h = setTimeout(() => {
      setQDeb(q.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(h);
  }, [q]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, total: t } = await apiFetch<TrashEntry[]>("contact/trash", {
        params: {
          limit: PAGE_SIZE,
          offset,
          ...(qDeb ? { q: qDeb } : {}),
        },
      });
      setRows(Array.isArray(data) ? data : []);
      setTotal(t ?? (Array.isArray(data) ? data.length : 0));
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, [offset, qDeb]);

  React.useEffect(() => {
    if (allowed) void load();
    else setLoading(false);
  }, [allowed, load]);

  const removeRow = (e: TrashEntry) => {
    setRows((prev) => prev.filter((r) => keyOf(r) !== keyOf(e)));
    setTotal((prev) => Math.max(0, prev - 1));
  };

  const restore = async (e: TrashEntry) => {
    setBusy(keyOf(e));
    setError(null);
    try {
      if (e.source === "merge" && e.mergeHistoryId != null) {
        await restoreMergeLoser(e.mergeHistoryId);
      } else {
        const res = await fetch(apiUrl(`contact/${e.id}/restore`), {
          method: "POST",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
      removeRow(e);
    } catch (err: any) {
      setError(String(err?.message ?? err));
    } finally {
      setBusy(null);
    }
  };

  const purge = async (e: TrashEntry) => {
    if (
      !(await bestaetigen({
        titel: "Endgültig löschen?",
        text: `„${e.name}" wird endgültig gelöscht. Das lässt sich nicht rückgängig machen.`,
        ja: "Endgültig löschen",
        ton: "danger",
      }))
    )
      return;
    setBusy(keyOf(e));
    setError(null);
    try {
      if (e.source === "merge" && e.mergeHistoryId != null) {
        await purgeMergeLoser(e.mergeHistoryId);
      } else {
        const res = await fetch(apiUrl(`contact/${e.id}/purge`), {
          method: "DELETE",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
      removeRow(e);
    } catch (err: any) {
      setError(String(err?.message ?? err));
    } finally {
      setBusy(null);
    }
  };

  if (!allowed) {
    return (
      <div className={styles.root}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft20Regular /> Zurück
        </button>
        <div className={styles.errorBox}>
          <Warning20Regular /> Der Papierkorb ist nur für Administratoren und die
          Geschäftsführung sichtbar.
        </div>
      </div>
    );
  }

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + rows.length, total);

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={goBack}>
        <ArrowLeft20Regular /> Zurück zu Kontakte
      </button>

      <div className={styles.bar}>
        <h1 className={styles.title}>Papierkorb</h1>
        <div className={styles.subtitle}>
          Gelöschte und zusammengeführte Kontakte. „Wiederherstellen" holt die
          Stammdaten zurück; „Endgültig löschen" entfernt sie unwiderruflich.
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Search20Regular />
          <input
            className={styles.searchInput}
            placeholder="Suche nach Name, Ort, Straße …"
            value={q}
            onChange={(ev) => setQ(ev.target.value)}
          />
        </div>
        <div className={styles.count}>
          {loading ? "Wird geladen…" : `${from}–${to} von ${total}`}
        </div>
      </div>

      {error && (
        <div className={styles.errorBox}>
          <Warning20Regular /> {error}
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.head}>
          <div>Typ</div>
          <div>Name</div>
          <div>Ort</div>
          <div>Straße</div>
          <div>Verantwortlich</div>
          <div>Grund</div>
          <div>Gelöscht</div>
          <div />
        </div>
        {loading ? (
          <div className={styles.empty}>Wird geladen…</div>
        ) : rows.length === 0 ? (
          <div className={styles.empty}>
            {qDeb ? "Keine Treffer." : "Der Papierkorb ist leer."}
          </div>
        ) : (
          rows.map((r) => (
            <div key={keyOf(r)} className={styles.row}>
              <div>
                <span className={styles.typeBadge}>{contactTypeLabel(r.type) || "—"}</span>
              </div>
              <div className={styles.cellName}>{r.name}</div>
              <div className={styles.cellMuted}>{r.city || "—"}</div>
              <div className={styles.cellMuted}>{r.street || "—"}</div>
              <div className={styles.cellMuted}>{r.responsibility}</div>
              <div>
                <span className={styles.reasonBadge}>{r.reason}</span>
              </div>
              <div className={styles.cellMuted}>{fmtDate(r.deletedAt)}</div>
              <div className={styles.actions}>
                <button
                  className={mergeClasses(styles.btn, styles.btnRestore)}
                  disabled={busy === keyOf(r)}
                  onClick={() => restore(r)}
                  title="Wiederherstellen"
                >
                  <ArrowUndo20Regular /> Wiederherstellen
                </button>
                <button
                  className={mergeClasses(styles.btn, styles.btnPurge)}
                  disabled={busy === keyOf(r)}
                  onClick={() => purge(r)}
                  title="Endgültig löschen"
                >
                  <Delete20Regular />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className={styles.pager}>
          <button
            className={styles.pagerBtn}
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Zurück
          </button>
          <span className={styles.count}>
            Seite {Math.floor(offset / PAGE_SIZE) + 1} von{" "}
            {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </span>
          <button
            className={styles.pagerBtn}
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Weiter
          </button>
        </div>
      )}
    </div>
  );
};

export default PapierkorbPage;
